import type { Home } from '@lmc/ts-common'
import { Button, Due, Spinner, Todo, theme } from '@lmc/ui-kit'
import { Text } from '@lmc/ui-kit/src/general'
import useMixpanelEvent, { MixpanelEvents } from 'hooks/useMixpanelEvent'
import { NAMESPACE } from 'locale'
import React from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { Link } from 'react-router-dom'
import styled from 'styled-components'
import Actions from '../../../actions'
import { REPORTS } from '../../../components/routes/reports'
import Operations from '../../../operations'
import { useStockDashboardSummary } from '../../../screens/Residents/screens/ResidentEmarV2Screens/hooks/useStockDashboardSummary'
import { HomeCard } from './index'
import MedicationBlob from './MedicationBlob'

type RoundRow = {
  time: string
  status: 'not-signed' | 'due' | 'signed'
  displayCount: number
  notAdministered: number
  prn: number
}

const MedicationCardV2 = () => {
  const { t } = useTranslation(NAMESPACE.EMAR_V2)
  const dispatch = useDispatch()
  const home: Home | undefined = useSelector(
    (state: { global: { home: Home } }) => state.global.home
  )
  const { data: dashboardData, isLoading: isLoadingStock } =
    useStockDashboardSummary()
  const sendEvent = useMixpanelEvent()

  const isLoading = isLoadingStock

  const rounds: RoundRow[] = dashboardData?.rounds
    ? dashboardData.rounds.map((round) => {
        const isOverdue = round.isOverdue

        const notSignedCount = isOverdue ? round.scheduled.notSigned : 0
        const dueCount = isOverdue ? 0 : round.scheduled.due
        const notAdminCount = round.scheduled.notAdministered
        const missedCount = round.scheduled.missed

        let status: RoundRow['status'] = 'signed'
        let displayCount = 0

        if (notSignedCount > 0) {
          status = 'not-signed'
          displayCount = missedCount
        } else if (dueCount > 0) {
          status = 'due'
          displayCount = dueCount
        }

        return {
          time: round.time,
          status,
          displayCount,
          notAdministered: notAdminCount,
          prn: round.PRN
        }
      })
    : []

  const getStatusIcon = (status: RoundRow['status']) => {
    if (status === 'not-signed') {
      return (
        <StatusIconContainer>
          <Todo color={theme.color.neutral.color5} />
        </StatusIconContainer>
      )
    }
    if (status === 'due') {
      return (
        <StatusIconContainer>
          <Due color={theme.color.status.warning} />
        </StatusIconContainer>
      )
    }
    return null
  }

  if (isLoading) {
    return (
      <HomeCard title={t('EMAR')}>
        <Spinner />
      </HomeCard>
    )
  }

  const hasRounds = rounds && rounds.length > 0
  const lowStockCount = dashboardData?.alerts?.lowStockCount || 0
  const outOfStockCount = dashboardData?.alerts?.outOfStockCount || 0
  const openDiscrepanciesCount =
    dashboardData?.alerts?.openDiscrepanciesLast30DaysCount || 0
  const hasStockAlerts = lowStockCount > 0 || outOfStockCount > 0
  const hasAlerts = hasStockAlerts || openDiscrepanciesCount > 0

  const openOnboardingModal = () => {
    dispatch(Operations.loadList('emar-round-timings'))
    dispatch(Operations.openModal({ modalId: 'onboardingEmar' }))
    dispatch(
      Actions.addForm({
        formId: 'onboardingEmar'
      })
    )
  }

  const shouldShowSetupButton =
    !hasRounds && !home?.features?.emarOnboarded?.enabled

  const handleLinkClick = (
    linkType: 'Administration' | 'Stock level' | 'Stock discrepancy'
  ) => {
    sendEvent(MixpanelEvents.EMAR_V2_HOMEPAGE_CARD_LINK_CLICKED, {
      homeId: home?.id,
      linkType,
      time: new Date().toISOString()
    })
  }

  return (
    <HomeCard title={t('EMAR')}>
      {hasRounds ? (
        <>
          <TableContainer>
            <Table>
              <thead>
                <TableHeaderRow>
                  <TableHeader>{t('TIME')}</TableHeader>
                  <TableHeader>{t('STATUS')}</TableHeader>
                  <TableHeader>{t('DETAILS')}</TableHeader>
                </TableHeaderRow>
              </thead>
              <tbody>
                {rounds.map((round) => (
                  <React.Fragment key={round.time}>
                    <TableRow>
                      <TableCell>{round.time}</TableCell>
                      <TableCell>
                        <StatusCell>
                          {getStatusIcon(round.status)}
                          <StatusText>
                            <strong>{round.displayCount}</strong>{' '}
                            {round.status === 'due'
                              ? t('DUE')
                              : t('NOT_SIGNED')}
                          </StatusText>
                        </StatusCell>
                      </TableCell>
                      <TableCell>
                        <DetailsCell>
                          <DetailText>
                            <strong>{round.notAdministered}</strong>{' '}
                            {t('NOT_ADMINISTERED')}
                          </DetailText>
                          <DetailText>
                            <strong>{round.prn}</strong> {t('PRN')}
                          </DetailText>
                        </DetailsCell>
                      </TableCell>
                    </TableRow>
                  </React.Fragment>
                ))}
              </tbody>
            </Table>
          </TableContainer>

          <FooterContainer>
            <Button
              variant="default"
              as={Link}
              to={REPORTS.EMAR_MEDICATION_ROUNDS}
              onClick={() => handleLinkClick('Administration')}
            >
              {t('VIEW_ALL')}
            </Button>
          </FooterContainer>

          {hasAlerts && (
            <AlertsContainer>
              {hasStockAlerts && (
                <AlertLink
                  to={REPORTS.EMAR_STOCK_LEVELS}
                  onClick={() => handleLinkClick('Stock level')}
                >
                  <Todo color={theme.color.status.warning} />
                  <AlertText>
                    {lowStockCount > 0 && outOfStockCount > 0 ? (
                      <>
                        <strong>{lowStockCount}</strong> {t('LOW_STOCK')}{' '}
                        {t('AND')} <strong>{outOfStockCount}</strong>{' '}
                        {t('OUT_OF_STOCK')} {t('MEDICATION')}
                      </>
                    ) : lowStockCount > 0 ? (
                      <>
                        <strong>{lowStockCount}</strong> {t('LOW_STOCK')}{' '}
                        {t('AND')} <strong>{outOfStockCount}</strong>{' '}
                        {t('OUT_OF_STOCK')} {t('MEDICATION')}
                      </>
                    ) : (
                      <>
                        <strong>{outOfStockCount}</strong> {t('OUT_OF_STOCK')}{' '}
                        {t('MEDICATION')}
                      </>
                    )}
                  </AlertText>
                </AlertLink>
              )}
              {openDiscrepanciesCount > 0 && (
                <AlertLink
                  to={REPORTS.EMAR_STOCK_DISCREPANCIES}
                  onClick={() => handleLinkClick('Stock discrepancy')}
                >
                  <Due />
                  <AlertText>
                    <strong>{openDiscrepanciesCount}</strong>{' '}
                    {t('OPEN_DISCREPANCIES_LAST_30_DAYS')}
                  </AlertText>
                </AlertLink>
              )}
            </AlertsContainer>
          )}
        </>
      ) : (
        <EmptyStateContainer>
          <EmptyStateInnerContainer>
            <EmptyStateTextContainer>
              <EmptyStateText weight="bold">
                {t('EMPTY_STATE_DESCRIPTION')}
              </EmptyStateText>
            </EmptyStateTextContainer>
            <EmptyStateIllustrationContainer>
              <MedicationBlob />
            </EmptyStateIllustrationContainer>
          </EmptyStateInnerContainer>
          {shouldShowSetupButton && (
            <EmptyStateButtonContainer>
              <Button variant="primary" onClick={openOnboardingModal}>
                {t('SET_UP_EMAR')}
              </Button>
            </EmptyStateButtonContainer>
          )}
        </EmptyStateContainer>
      )}
    </HomeCard>
  )
}

const TableContainer = styled.div`
  width: 100%;
  margin-bottom: 4px;
`

const Table = styled.table`
  margin-top: 12px;
  width: 100%;
  border-collapse: collapse;
`

const TableHeaderRow = styled.tr``

const TableHeader = styled.th`
  text-align: left;
  font-size: 14px;
  font-weight: 700;
  color: ${({ theme }) => theme.color.neutral.color1};
  padding-right: 16px;

  &:last-child {
    padding-right: 0;
  }
`

const TableRow = styled.tr`
  border-bottom: 1px solid ${({ theme }) => theme.color.neutral.color7};

  &:last-child {
    border-bottom: none;
  }
`

const TableCell = styled.td`
  padding: 8px 0;
  padding-right: 16px;
  font-size: 14px;
  color: ${({ theme }) => theme.color.neutral.color3};

  &:last-child {
    padding-right: 0;
  }
`

const StatusCell = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`

const StatusText = styled(Text)`
  font-size: 14px;
  color: ${({ theme }) => theme.color.neutral.color4};
  font-weight: 400;

  strong {
    font-weight: 700;
  }
`

const DetailsCell = styled.div`
  display: flex;
  gap: 16px;
  justify-content: space-between;
`

const DetailText = styled(Text)`
  font-size: 14px;
  color: ${({ theme }) => theme.color.neutral.color4};
  font-weight: 400;

  strong {
    font-weight: 700;
  }
`

const FooterContainer = styled.div`
  display: flex;
  justify-content: flex-end;
`

const AlertsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`

const AlertLink = styled(Link)`
  display: flex;
  align-items: center;
  gap: 8px;
  text-decoration: none;
  color: inherit;
  cursor: pointer;

  &:hover {
    text-decoration: underline;
    text-decoration-color: ${({ theme }) => theme.color.neutral.color4};
  }
`

const AlertText = styled(Text)`
  font-size: 14px;
  color: ${({ theme }) => theme.color.neutral.color4};

  strong {
    font-weight: 700;
  }
`

const EmptyStateContainer = styled.div`
  display: flex;
  flex-direction: column;
`

const EmptyStateInnerContainer = styled.div`
  margin: 16px 0 24px 0;
  display: flex;
  align-items: center;
  gap: 24px;
  padding: 24px;
  background-color: ${({ theme }) => theme.color.status.secondary};
  border-radius: 16px;
`

const EmptyStateTextContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  flex: 1;
`

const EmptyStateIllustrationContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`

const EmptyStateButtonContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: auto;
`

const StatusIconContainer = styled.div`
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
`

const EmptyStateText = styled(Text)`
  color: ${({ theme }) => theme.color.background.primary};
  line-height: 24px;
`

export default MedicationCardV2
